import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link as RouterLink } from 'react-router-dom';
import {
  getInstagramProspects,
  lookupInstagramProfiles,
  updateInstagramProspectChatPrompts,
  updateInstagramProspectColors,
} from '../services/api';
import { getApiErrorMessage } from '../../../../utils/apiError';
import {
  formatInstagramMetric,
  buildProspectPalettePreview,
  MAX_PROSPECT_COLORS,
  MAX_INSTAGRAM_PROFILES,
  normalizeProspectColors,
  sourceLabel,
  splitInstagramProfiles,
} from './instagramPage.utils';

const PAGE_BG = 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)';
const CARD_BORDER = '1px solid rgba(148, 163, 184, 0.22)';

const SourceChip = ({ source }) => {
  const label = sourceLabel(source);
  if (!label) return null;
  const isGoogle = source === 'google';
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 700,
        bgcolor: isGoogle ? '#eff6ff' : '#fdf2f8',
        color: isGoogle ? '#1d4ed8' : '#be185d',
      }}
    />
  );
};

const Metric = ({ icon, label, value }) => (
  <Box
    sx={{
      minWidth: 0,
      flex: 1,
      p: 1.25,
      borderRadius: 2,
      bgcolor: '#f8fafc',
      border: '1px solid #e2e8f0',
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center" color="#64748b">
      {icon}
      <Typography variant="caption" fontWeight={700}>
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ mt: 0.5, fontWeight: 800, color: '#0f172a' }}>
      {formatInstagramMetric(value)}
    </Typography>
  </Box>
);

const ContactRow = ({ icon, label, value, source, href }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box sx={{ color: '#64748b', pt: 0.2 }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>
        <SourceChip source={source} />
      </Stack>
      {value ? (
        href ? (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              overflowWrap: 'anywhere',
            }}
          >
            {value}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        ) : (
          <Typography sx={{ color: '#0f172a', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
            {value}
          </Typography>
        )
      ) : (
        <Typography variant="body2" color="text.secondary">
          Não disponível
        </Typography>
      )}
    </Box>
  </Stack>
);

const ChatPromptsEditor = ({ result, username, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [customerPrompt, setCustomerPrompt] = useState(result.customerChatPrompt || '');
  const [foodstationPrompt, setFoodstationPrompt] = useState(result.foodstationChatPrompt || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (editing) return;
    setCustomerPrompt(result.customerChatPrompt || '');
    setFoodstationPrompt(result.foodstationChatPrompt || '');
  }, [editing, result.customerChatPrompt, result.foodstationChatPrompt]);

  const savePrompts = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await onSave(username, {
        customerChatPrompt: customerPrompt,
        foodstationChatPrompt: foodstationPrompt,
      });
      setEditing(false);
    } catch (requestError) {
      setSaveError(getApiErrorMessage(
        requestError,
        'Não foi possível salvar os prompts das conversas.',
      ));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Button
        type="button"
        variant="outlined"
        fullWidth
        startIcon={<EditOutlinedIcon />}
        onClick={() => setEditing(true)}
        sx={{ mt: 2, borderRadius: 2, fontWeight: 750 }}
      >
        Editar prompts das conversas
      </Button>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <Typography fontWeight={800} color="#0f172a">
        Prompts do preview de WhatsApp
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Deixe em branco para usar o prompt global. Se preenchido, este texto tem prioridade.
        O prompt do cliente continua concatenado com os dados deste estabelecimento.
      </Typography>
      <Stack spacing={2}>
        <TextField
          fullWidth
          multiline
          minRows={7}
          label="Prompt da conversa do cliente"
          value={customerPrompt}
          disabled={saving}
          inputProps={{ maxLength: 20000, 'aria-label': 'Prompt da conversa do cliente' }}
          helperText={customerPrompt.trim()
            ? 'Prompt específico deste prospecto. Será concatenado com os dados atuais do estabelecimento.'
            : 'Vazio: usa o prompt global. O servidor concatena os dados deste estabelecimento.'}
          onChange={(event) => setCustomerPrompt(event.target.value)}
        />
        <TextField
          fullWidth
          multiline
          minRows={7}
          label="Prompt da conversa com a FoodStation"
          value={foodstationPrompt}
          disabled={saving}
          inputProps={{ maxLength: 20000, 'aria-label': 'Prompt da conversa com a FoodStation' }}
          helperText={foodstationPrompt.trim()
            ? 'Prompt específico deste prospecto. Usado sozinho, sem concatenar os dados do estabelecimento.'
            : 'Vazio: usa o prompt global da FoodStation.'}
          onChange={(event) => setFoodstationPrompt(event.target.value)}
        />
        {saveError && <Alert severity="error">{saveError}</Alert>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            type="button"
            disabled={saving}
            onClick={() => {
              setCustomerPrompt(result.customerChatPrompt || '');
              setFoodstationPrompt(result.foodstationChatPrompt || '');
              setSaveError('');
              setEditing(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveOutlinedIcon />}
            onClick={savePrompts}
          >
            {saving ? 'Salvando...' : 'Salvar prompts'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const ProspectPalettePreview = ({ colors, profile }) => {
  const theme = buildProspectPalettePreview(colors);
  const name = profile?.name || profile?.instagram || 'Seu negócio';
  const bio = profile?.bio || 'Sua descrição aparecerá aqui.';
  const initials = String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
  const actions = [
    'Falar no WhatsApp',
    (profile?.website || profile?.links?.length) && 'Visitar nosso site',
    'Ver no Instagram',
  ].filter(Boolean);

  return (
    <Box
      aria-label="Prévia das cores do LinkPage"
      sx={{
        width: '100%',
        maxWidth: 250,
        mx: 'auto',
        mt: 1.25,
        bgcolor: theme.pageBg,
        borderRadius: 3,
        p: 1.15,
      }}
    >
      <Box
        sx={{
          bgcolor: theme.cardBg,
          borderRadius: 2.4,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.22)',
        }}
      >
        <Box sx={{ height: 76, background: theme.cover }} />
        <Box sx={{ px: 1.5, pb: 1.25, textAlign: 'center' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              mx: 'auto',
              mt: -3.1,
              mb: 0.7,
              borderRadius: '50%',
              bgcolor: theme.cardBg,
              border: `3px solid ${theme.cardBg}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.text,
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {profile?.profileImage ? (
              <Box
                component="img"
                src={profile.profileImage}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : initials}
          </Box>
          <Typography
            sx={{ color: theme.text, fontSize: 11.5, lineHeight: 1.15, fontWeight: 850 }}
          >
            {name}
          </Typography>
          <Typography
            sx={{
              color: theme.text,
              opacity: 0.78,
              fontSize: 7.5,
              lineHeight: 1.25,
              mt: 0.6,
              mb: 1.25,
              px: 0.6,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              minHeight: 19,
            }}
          >
            {bio}
          </Typography>
          {actions.slice(0, 3).map((label) => (
            <Box
              key={label}
              sx={{
                minHeight: 30,
                mt: 0.65,
                px: 0.75,
                borderRadius: 1.5,
                bgcolor: theme.accent,
                color: theme.accentText,
                border: '1px solid rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: 1,
                  bgcolor: theme.cardBg,
                  color: theme.text,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 11,
                  flex: '0 0 auto',
                }}
              >
                ↗
              </Box>
              <Typography sx={{ fontSize: 8, fontWeight: 800, flex: 1, textAlign: 'left' }}>
                {label}
              </Typography>
              <Typography aria-hidden sx={{ fontSize: 14, lineHeight: 1 }}>›</Typography>
            </Box>
          ))}
          <Typography sx={{ color: theme.text, opacity: 0.62, fontSize: 6.5, mt: 1.25 }}>
            Powered by <Box component="span" sx={{ fontWeight: 850 }}>FoodStation</Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ColorPalette = ({ colors, username, profile, onSave }) => {
  const [copiedColor, setCopiedColor] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => normalizeProspectColors(colors));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const palette = useMemo(() => normalizeProspectColors(colors), [colors]);
  const visiblePalette = useMemo(
    () => (palette.length ? palette : buildProspectPalettePreview([]).palette),
    [palette],
  );

  useEffect(() => {
    if (!editing) setDraft(palette);
  }, [editing, palette]);

  const copyColor = async (color) => {
    try {
      await navigator.clipboard.writeText(color);
      setCopiedColor(color);
    } catch {
      setCopiedColor('');
    }
  };

  const startEditing = () => {
    setDraft(palette.length ? palette : visiblePalette);
    setSaveError('');
    setEditing(true);
  };

  const updateDraftColor = (index, value) => {
    setDraft((current) => current.map((color, itemIndex) => (
      itemIndex === index ? String(value).toUpperCase() : color
    )));
  };

  const saveColors = async () => {
    if (!draft.length || draft.some((color) => !/^#[0-9A-F]{6}$/i.test(color))) {
      setSaveError('Use ao menos uma cor no formato #RRGGBB.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await onSave(username, normalizeProspectColors(draft));
      setEditing(false);
    } catch (requestError) {
      setSaveError(getApiErrorMessage(requestError, 'Não foi possível salvar as cores.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Cores do LinkPage
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            A mais escura forma o card; as claras compõem capa e botões.
          </Typography>
        </Box>
        {!editing && (
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={startEditing}>
            Editar
          </Button>
        )}
      </Stack>

      <ProspectPalettePreview
        colors={editing ? draft : visiblePalette}
        profile={profile}
      />

      {!editing && (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {visiblePalette.map((color) => (
          <Tooltip key={color} title={copiedColor === color ? 'Copiado!' : 'Copiar cor'}>
            <ButtonBase
              type="button"
              aria-label={`Copiar cor ${color}`}
              onClick={() => copyColor(color)}
              sx={{
                gap: 0.75,
                px: 1,
                py: 0.65,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                bgcolor: '#fff',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: color,
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                }}
              />
              <Typography component="span" variant="caption" fontWeight={750} color="#334155">
                {color.toUpperCase()}
              </Typography>
            </ButtonBase>
          </Tooltip>
        ))}
      </Stack>
      )}

      {editing && (
        <Stack spacing={1} sx={{ mt: 1.25 }}>
          {draft.map((color, index) => {
            const valid = /^#[0-9A-F]{6}$/i.test(color);
            return (
              <Stack key={`${index}-${draft.length}`} direction="row" spacing={1} alignItems="center">
                <Box
                  component="input"
                  type="color"
                  aria-label={`Selecionar cor ${index + 1}`}
                  value={valid ? color : '#000000'}
                  onChange={(event) => updateDraftColor(index, event.target.value)}
                  sx={{ width: 42, height: 38, p: 0.25, border: '1px solid #cbd5e1', borderRadius: 1.5 }}
                />
                <TextField
                  size="small"
                  value={color}
                  error={!valid}
                  inputProps={{ 'aria-label': `Hexadecimal da cor ${index + 1}`, maxLength: 7 }}
                  onChange={(event) => updateDraftColor(index, event.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button
                  color="error"
                  size="small"
                  aria-label={`Remover cor ${index + 1}`}
                  disabled={draft.length === 1 || saving}
                  onClick={() => setDraft((current) => current.filter((_item, itemIndex) => itemIndex !== index))}
                  sx={{ minWidth: 38, px: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </Stack>
            );
          })}
          {draft.length < MAX_PROSPECT_COLORS && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={saving}
              onClick={() => setDraft((current) => [...current, '#FFFFFF'])}
            >
              Adicionar cor
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            Até {MAX_PROSPECT_COLORS} cores. A prévia acima é atualizada enquanto você edita.
          </Typography>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              disabled={saving}
              onClick={() => {
                setEditing(false);
                setSaveError('');
              }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveOutlinedIcon />}
              disabled={saving}
              onClick={saveColors}
            >
              {saving ? 'Salvando...' : 'Salvar cores'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const ProfileCard = ({
  result,
  onSaveColors,
  onSaveChatPrompts,
  collapsible = false,
  defaultCollapsed = false,
}) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  if (result.status === 'error') {
    return (
      <Card
        sx={{ border: CARD_BORDER, borderRadius: 3, boxShadow: '0 8px 24px rgba(15,23,42,0.05)' }}
      >
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: '#fee2e2', color: '#dc2626' }}>
              <InstagramIcon />
            </Avatar>
            <Box>
              <Typography fontWeight={800}>{result.instagram || result.input}</Typography>
              <Typography variant="caption" color="text.secondary">
                Consulta não concluída
              </Typography>
            </Box>
          </Stack>
          <Alert severity="error">
            {result.error?.message || 'Não foi possível consultar este perfil.'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const previewUsername = String(result.instagram || '').replace(/^@/, '').toLowerCase();

  return (
    <Card
      sx={{
        height: expanded ? '100%' : 'auto',
        border: CARD_BORDER,
        borderRadius: 3,
        boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
      }}
    >
      <CardContent
        sx={{
          p: expanded ? { xs: 2, sm: 2.5 } : 1.5,
          '&:last-child': { pb: expanded ? { xs: 2, sm: 2.5 } : 1.5 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            src={result.profileImage || undefined}
            alt={result.name || result.instagram}
            sx={{
              width: expanded ? 64 : 48,
              height: expanded ? 64 : 48,
              bgcolor: '#fce7f3',
              color: '#be185d',
              transition: 'width 160ms ease, height 160ms ease',
            }}
          >
            <InstagramIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.05rem' }} noWrap>
              {result.name || result.instagram}
            </Typography>
            <Link
              href={`https://www.instagram.com/${String(result.instagram || '').replace(/^@/, '')}/`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {result.instagram}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
            {result.lastSearchedAt && (
              <Typography variant="caption" color="text.secondary" display="block">
                Última consulta: {new Date(result.lastSearchedAt).toLocaleString('pt-BR')}
              </Typography>
            )}
          </Box>
          {collapsible && (
            <Tooltip title={expanded ? 'Recolher detalhes' : 'Ver detalhes'}>
              <IconButton
                type="button"
                size="small"
                aria-label={`${expanded ? 'Recolher' : 'Expandir'} detalhes de ${result.instagram}`}
                aria-expanded={expanded}
                onClick={() => setExpanded((current) => !current)}
                sx={{ color: '#475569', flex: '0 0 auto' }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {!expanded && (
          <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mt: 1, pl: 0.25 }}>
            {normalizeProspectColors(result.colors).map((color) => (
              <Box
                key={color}
                aria-label={`Cor ${color}`}
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: color,
                  border: '1px solid rgba(15,23,42,0.2)',
                }}
              />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Clique para ver detalhes e editar
            </Typography>
          </Stack>
        )}

        {expanded && (
          <>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, minHeight: 40, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {result.bio || 'Bio não disponível.'}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ my: 2 }}>
          <Metric
            icon={<PeopleAltOutlinedIcon fontSize="inherit" />}
            label="Seguidores"
            value={result.followers}
          />
          <Metric
            icon={<PersonAddAltOutlinedIcon fontSize="inherit" />}
            label="Seguindo"
            value={result.following}
          />
          <Metric
            icon={<GridOnOutlinedIcon fontSize="inherit" />}
            label="Publicações"
            value={result.posts}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack spacing={1.75}>
          <ContactRow
            icon={<StorefrontOutlinedIcon fontSize="small" />}
            label="Tipo de negócio"
            value={result.businessType}
            source={result.sources?.businessType}
          />
          <ContactRow
            icon={<PhoneOutlinedIcon fontSize="small" />}
            label="Telefone"
            value={result.phone}
            source={result.sources?.phone}
          />
          <ContactRow
            icon={<LanguageOutlinedIcon fontSize="small" />}
            label="Site"
            value={result.website}
            href={result.website}
            source={result.sources?.website}
          />
          <ContactRow
            icon={<PlaceOutlinedIcon fontSize="small" />}
            label="Endereço"
            value={result.address}
            source={result.sources?.address}
          />
          <ContactRow
            icon={<ScheduleOutlinedIcon fontSize="small" />}
            label="Horários"
            value={result.hours}
            source={result.sources?.hours}
          />
        </Stack>

        <ColorPalette
          colors={result.colors}
          username={previewUsername}
          profile={result}
          onSave={onSaveColors}
        />

        {previewUsername && (
          <ChatPromptsEditor
            result={result}
            username={previewUsername}
            onSave={onSaveChatPrompts}
          />
        )}

        {previewUsername && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Button
              component="a"
              href={`/links/preview/${encodeURIComponent(previewUsername)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              fullWidth
              startIcon={<PreviewOutlinedIcon />}
              sx={{ borderRadius: 2, fontWeight: 750 }}
            >
              Ver preview do LinkPage
            </Button>
            <Button
              component="a"
              href={`/preview/whatsapp/${encodeURIComponent(previewUsername)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              fullWidth
              startIcon={<ChatOutlinedIcon />}
              sx={{ borderRadius: 2, fontWeight: 750 }}
            >
              Ver preview das conversas
            </Button>
          </Stack>
        )}

        {result.warnings?.map((warning) => (
          <Alert key={`${warning.code}-${warning.message}`} severity="warning" sx={{ mt: 2 }}>
            {warning.message}
          </Alert>
        ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const InstagramProfilesPage = () => {
  const [input, setInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [prospects, setProspects] = useState([]);
  const [prospectsTotal, setProspectsTotal] = useState(0);
  const [prospectsLoading, setProspectsLoading] = useState(true);
  const [prospectsError, setProspectsError] = useState('');
  const profiles = useMemo(() => splitInstagramProfiles(input), [input]);
  const overLimit = profiles.length > MAX_INSTAGRAM_PROFILES;

  const loadProspects = useCallback(async () => {
    setProspectsLoading(true);
    setProspectsError('');
    try {
      const data = await getInstagramProspects({ page: 1, pageSize: 100 });
      setProspects(Array.isArray(data?.items) ? data.items : []);
      setProspectsTotal(Number(data?.total) || 0);
    } catch (requestError) {
      setProspectsError(getApiErrorMessage(
        requestError,
        'Não foi possível carregar os negócios pesquisados.',
      ));
    } finally {
      setProspectsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  const handleSaveColors = useCallback(async (username, colors) => {
    const updated = await updateInstagramProspectColors(username, colors);
    const matchesUsername = (item) => (
      String(item?.instagram || '').replace(/^@/, '').toLowerCase() === username
    );
    const mergeUpdated = (items) => items.map((item) => (
      matchesUsername(item) ? { ...item, ...updated } : item
    ));
    setProspects(mergeUpdated);
    setResults(mergeUpdated);
    return updated;
  }, []);

  const handleSaveChatPrompts = useCallback(async (username, prompts) => {
    const updated = await updateInstagramProspectChatPrompts(username, prompts);
    const matchesUsername = (item) => (
      String(item?.instagram || '').replace(/^@/, '').toLowerCase() === username
    );
    const mergeUpdated = (items) => items.map((item) => (
      matchesUsername(item) ? { ...item, ...updated } : item
    ));
    setProspects(mergeUpdated);
    setResults(mergeUpdated);
    return updated;
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!profiles.length) {
      setError('Informe ao menos um perfil do Instagram.');
      return;
    }
    if (overLimit) {
      setError(`Informe no máximo ${MAX_INSTAGRAM_PROFILES} perfis por consulta.`);
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);
    try {
      const data = await lookupInstagramProfiles(profiles);
      setResults(Array.isArray(data?.results) ? data.results : []);
      await loadProspects();
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          'Não foi possível consultar os perfis. Tente novamente em instantes.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100%', p: { xs: 1.5, sm: 2, md: 3 }, background: PAGE_BG }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            border: CARD_BORDER,
            boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            mb={1}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: '#fce7f3', color: '#be185d' }}>
                <InstagramIcon />
              </Avatar>
              <Box>
                <Typography component="h1" variant="h5" fontWeight={850} color="#0f172a">
                  Consulta de perfis do Instagram
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Consulte dados públicos e complemente telefone ou site pelo Google Places quando
                  necessário.
                </Typography>
              </Box>
            </Stack>
            <Button
              component={RouterLink}
              to="/manager/whatsapp-preview-prompts"
              variant="outlined"
              startIcon={<ChatOutlinedIcon />}
              sx={{ borderRadius: 2, fontWeight: 750, flex: '0 0 auto' }}
            >
              Prompts globais
            </Button>
          </Stack>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              label="Perfis do Instagram"
              placeholder={'@restaurante1\n@restaurante2\nhttps://instagram.com/restaurante3'}
              value={input}
              disabled={loading}
              error={overLimit}
              helperText={
                overLimit
                  ? `O limite é de ${MAX_INSTAGRAM_PROFILES} perfis por consulta.`
                  : 'Informe um perfil por linha. URLs completas também são aceitas.'
              }
              onChange={(event) => setInput(event.target.value)}
              inputProps={{ 'aria-label': 'Perfis do Instagram' }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              mt={1.5}
            >
              <Typography variant="body2" color={overLimit ? 'error.main' : 'text.secondary'}>
                {profiles.length}/{MAX_INSTAGRAM_PROFILES} perfis
              </Typography>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !profiles.length || overLimit}
                startIcon={
                  loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />
                }
                sx={{ minWidth: 150, borderRadius: 2, fontWeight: 750 }}
              >
                {loading ? 'Consultando...' : 'Consultar'}
              </Button>
            </Stack>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {loading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Consultando o Instagram e complementando os campos ausentes. Isso pode levar alguns
              segundos.
            </Alert>
          )}
        </Box>

        {results.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography component="h2" variant="h6" fontWeight={800} color="#0f172a" mb={1.5}>
              Resultados desta consulta ({results.length})
            </Typography>
            <Grid container spacing={2}>
              {results.map((result, index) => (
                <Grid item xs={12} md={6} key={`${result.instagram || result.input}-${index}`}>
                  <ProfileCard
                    result={result}
                    onSaveColors={handleSaveColors}
                    onSaveChatPrompts={handleSaveChatPrompts}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1}
            mb={1.5}
          >
            <Box>
              <Typography component="h2" variant="h6" fontWeight={800} color="#0f172a">
                Negócios pesquisados ({prospectsTotal})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Perfis salvos e atualizados automaticamente a cada consulta.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={loadProspects}
              disabled={prospectsLoading}
            >
              {prospectsLoading ? 'Atualizando...' : 'Atualizar lista'}
            </Button>
          </Stack>

          {prospectsError && <Alert severity="error">{prospectsError}</Alert>}
          {prospectsLoading && !prospects.length && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!prospectsLoading && !prospectsError && !prospects.length && (
            <Alert severity="info">Nenhum negócio pesquisado ainda.</Alert>
          )}
          {prospects.length > 0 && (
            <Grid container spacing={2}>
              {prospects.map((prospect) => (
                <Grid item xs={12} md={6} key={prospect.id || prospect.instagram}>
                  <ProfileCard
                    result={prospect}
                    onSaveColors={handleSaveColors}
                    onSaveChatPrompts={handleSaveChatPrompts}
                    collapsible
                    defaultCollapsed
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InstagramProfilesPage;
